import React, { useRef, useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { validateFunc } from '../../constraints/constraints'
import { withTranslation } from 'react-i18next'
import { createCuisine, editCuisine, getCuisines } from '../../apollo'
import ConfigurableValues from '../../config/constants'
import useStyles from './styles'
import useGlobalStyles from '../../utils/globalStyles'
import { Box, Typography, Input, Button, Alert, Grid } from '@mui/material'
import FormCardHeader from '../FormCardHeader'

const CREATE_CUISINE = gql`
  ${createCuisine}
`
const EDIT_CUISINE = gql`
  ${editCuisine}
`
const GET_CUISINES = gql`
  ${getCuisines}
`

// Quick-pick icons for the "Explore your options" row in the customer app
const EMOJI_SUGGESTIONS = [
  '🍚',
  '🫒',
  '🥬',
  '🍗',
  '🐟',
  '🥩',
  '🥤',
  '🍪',
  '🍞',
  '🧂',
  '🍲',
  '🍛'
]

function Category(props) {
  const formRef = useRef()
  const fileInputRef = useRef()
  const { CLOUDINARY_UPLOAD_URL, CLOUDINARY_FOOD } = ConfigurableValues()
  const name = props.cuisine ? props.cuisine.name : ''
  const description = props.cuisine ? props.cuisine.description : ''
  // Circular artwork shown in the customer app's "Top cuisines" row
  const [image, setImage] = useState(
    props.cuisine ? props.cuisine.image || '' : ''
  )
  const [emoji, setEmoji] = useState(
    props.cuisine ? props.cuisine.emoji || '' : ''
  )
  const [order, setOrder] = useState(
    props.cuisine &&
      props.cuisine.order !== null &&
      props.cuisine.order !== undefined
      ? String(props.cuisine.order)
      : '0'
  )
  const [imageLoading, setImageLoading] = useState(false)
  const mutation = props.cuisine ? EDIT_CUISINE : CREATE_CUISINE
  const [mainError, mainErrorSetter] = useState('')
  const [success, successSetter] = useState('')
  const [nameError, setNameError] = useState(null)
  const [descriptionError, setDescriptionError] = useState(null)
  const onBlur = (setter, field, state) => {
    setter(!validateFunc({ [field]: state }, field))
  }
  const onCompleted = data => {
    console.log('Data => ', data)
    const message = props.cuisine ? t('CuisineUpdated') : t('CuisineAdded')
    successSetter(message)
    mainErrorSetter('')
    if (!props.cuisine) clearFields()
  }
  const onError = error => {
    console.log('Error => ', error)
    let message = ''
    try {
      message = error.graphQLErrors[0].message
    } catch (err) {
      message = t('ActionFailedTryAgain')
    }
    successSetter('')
    mainErrorSetter(message)
  }
  const [mutate, { loading }] = useMutation(mutation, {
    refetchQueries: [{ query: GET_CUISINES }],
    onError,
    onCompleted
  })

  const onSubmitValidaiton = () => {
    const nameError = !validateFunc(
      { name: formRef.current['input-name'].value },
      'name'
    )
    const descriptionError = !validateFunc(
      { description: formRef.current['input-description'].value },
      'description'
    )
    setNameError(nameError)
    setDescriptionError(descriptionError)
    return nameError && descriptionError
  }
  const clearFields = () => {
    formRef.current.reset()
    setImage('')
    setEmoji('')
    setOrder('0')
    setNameError(null)
    setDescriptionError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Only one image per cuisine: picking a new file replaces whatever was set
  // before (uploaded file or pasted url)
  const onSelectImage = event => {
    const picked = Array.from(event.target.files || []).find(file =>
      file.name.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)
    )
    // Let the same file be picked again after a remove
    event.target.value = ''
    if (!picked) return

    if (picked.size > 5 * 1024 * 1024) {
      mainErrorSetter('File size should be less than or equal to 5MB')
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      mainErrorSetter('')
      setImage(reader.result)
    }
    reader.readAsDataURL(picked)
  }

  const removeImage = () => {
    setImage('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Base64 previews get pushed to cloudinary, plain urls are kept as they are
  const uploadImageToCloudinary = async () => {
    if (!image) return ''
    if (!image.startsWith('data:')) return image.trim()

    setImageLoading(true)
    try {
      const result = await fetch(`${CLOUDINARY_UPLOAD_URL}/image/upload`, {
        body: JSON.stringify({ file: image, upload_preset: CLOUDINARY_FOOD }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST'
      })
      const imageData = await result.json()
      if (!imageData.secure_url) throw new Error('No url returned')
      return imageData.secure_url
    } catch (e) {
      console.error('Cuisine image upload error => ', e)
      mainErrorSetter('Failed to upload image to Cloudinary.')
      return null
    } finally {
      setImageLoading(false)
    }
  }

  const { t } = props
  const classes = useStyles()
  const globalClasses = useGlobalStyles()

  return (
    <Box container className={classes.container}>
      <FormCardHeader
        className={classes.heading}
        title={props.cuisine ? t('EditCuisine') : t('AddCuisine')}
        onClose={props.onClose}
      />
      <Box className={classes.form}>
        <form ref={formRef}>
          <Box className={globalClasses.flexRow}>
            <Grid container columnSpacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.labelText}>
                  {t('Name')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-name"
                  name="input-name"
                  placeholder={t('Name')}
                  type="text"
                  defaultValue={name}
                  onBlur={event =>
                    onBlur(setNameError, 'name', event.target.value)
                  }
                  disableUnderline
                  className={[
                    globalClasses.input,
                    nameError === false
                      ? globalClasses.inputError
                      : nameError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.labelText}>
                  {t('Description')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-description"
                  name="input-description"
                  placeholder={t('Description')}
                  type="text"
                  defaultValue={description}
                  onBlur={event => {
                    onBlur(
                      setDescriptionError,
                      'description',
                      event.target.value
                    )
                  }}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    descriptionError === false
                      ? globalClasses.inputError
                      : descriptionError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Grid>
            </Grid>
          </Box>

          <Box className={globalClasses.flexRow}>
            <Grid container columnSpacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.labelText}>
                  {t('ImageUrl')} ({t('Optional')})
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-image"
                  name="input-image"
                  placeholder={t('PHShopTypeImage')}
                  type="text"
                  value={image.startsWith('data:') ? '' : image}
                  onChange={event => setImage(event.target.value)}
                  disabled={image.startsWith('data:')}
                  disableUnderline
                  className={globalClasses.input}
                />
                <Typography
                  className={classes.labelText}
                  style={{ opacity: 0.7, fontSize: 13 }}>
                  {t('CuisineImageHelp')}
                </Typography>
                <Box
                  mt={1}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginLeft: '4%'
                  }}>
                  {image ? (
                    <img
                      key={image}
                      src={image}
                      alt={t('Preview')}
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: 12,
                        objectFit: 'cover'
                      }}
                      onError={event => {
                        event.target.style.visibility = 'hidden'
                      }}
                    />
                  ) : null}
                  <Box>
                    <label
                      htmlFor={
                        props.cuisine
                          ? `edit-cuisine-image-${props.cuisine._id}`
                          : 'add-cuisine-image'
                      }
                      className={classes.fileUpload}>
                      {t('UploadAnImage')}
                    </label>
                    <input
                      ref={fileInputRef}
                      className={classes.file}
                      id={
                        props.cuisine
                          ? `edit-cuisine-image-${props.cuisine._id}`
                          : 'add-cuisine-image'
                      }
                      type="file"
                      accept="image/*"
                      onChange={onSelectImage}
                    />
                    {image ? (
                      <Button
                        type="button"
                        onClick={removeImage}
                        style={{
                          marginLeft: 8,
                          fontSize: 12,
                          textTransform: 'none'
                        }}>
                        {t('Remove')}
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.labelText}>
                  {t('Emoji')} ({t('Optional')})
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-cuisine-emoji"
                  name="input-cuisine-emoji"
                  placeholder={t('PHCuisineEmoji')}
                  type="text"
                  value={emoji}
                  onChange={event => setEmoji(event.target.value.trim())}
                  disableUnderline
                  className={globalClasses.input}
                />
                <Box
                  mt={1}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    alignItems: 'center'
                  }}>
                  {EMOJI_SUGGESTIONS.map(suggestion => (
                    <Button
                      key={suggestion}
                      type="button"
                      onClick={() =>
                        setEmoji(current =>
                          current === suggestion ? '' : suggestion
                        )
                      }
                      style={{
                        minWidth: 36,
                        height: 36,
                        padding: 0,
                        fontSize: 18,
                        lineHeight: '36px',
                        borderRadius: 8,
                        border:
                          emoji === suggestion
                            ? '2px solid #F6985D'
                            : '1px solid #E0E0E0',
                        backgroundColor:
                          emoji === suggestion ? '#FFF3E9' : 'transparent'
                      }}>
                      {suggestion}
                    </Button>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box className={globalClasses.flexRow}>
            <Grid container columnSpacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.labelText}>
                  {t('DisplayOrder')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-cuisine-order"
                  name="input-cuisine-order"
                  placeholder="0"
                  type="number"
                  value={order}
                  onChange={event => setOrder(event.target.value)}
                  disableUnderline
                  className={globalClasses.input}
                />
              </Grid>
            </Grid>
          </Box>

          {loading || imageLoading ? t('Loading') : null}
          <Box>
            <Button
              className={globalClasses.button}
              disabled={loading || imageLoading}
              onClick={async e => {
                e.preventDefault()
                if (onSubmitValidaiton() && !loading && !imageLoading) {
                  const uploadedImage = await uploadImageToCloudinary()
                  // Upload failed: keep the form as is so nothing is lost
                  if (uploadedImage === null) return
                  mutate({
                    variables: {
                      cuisineInput: {
                        _id: props.cuisine ? props.cuisine._id : '',
                        name: formRef.current['input-name'].value,
                        description: formRef.current['input-description'].value,
                        // Replaces the previous image, or clears it when removed
                        image: uploadedImage,
                        emoji,
                        order: Number(order) || 0
                      }
                    }
                  })
                }
              }}>
              {t('Save')}
            </Button>
          </Box>
        </form>
        <Box mt={2}>
          {success && (
            <Alert
              className={globalClasses.alertSuccess}
              variant="filled"
              severity="success">
              {success}
            </Alert>
          )}
          {mainError && (
            <Alert
              className={globalClasses.alertError}
              variant="filled"
              severity="error">
              {mainError}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default withTranslation()(Category)
